import { useEffect, useState } from 'react';
import InstagramIcon from '@mui/icons-material/Instagram';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { fetchPostWiseBifurcation } from 'api_handlers/planner2';
import { ui } from '../shared/insightUi';

const PERIOD_OPTIONS = [
  { value: '28', label: '28 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: 'lifetime', label: 'Lifetime' },
];
const CONTENT_OPTIONS = [
  { value: 'overall', label: 'Overall' },
  { value: 'video', label: 'Videos' },
  { value: 'short', label: 'Shorts' },
];
// Non-BrandConnect YouTube. Sent as the *_post_type param, where the API
// maps `video`/`short` onto YoutubeVideo.video_type. Live streams belong to
// neither tab, so Videos + Shorts is less than All on channels that stream.
const YT_CONTENT_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'video', label: 'Videos' },
  { value: 'short', label: 'Shorts' },
];

const POST_COUNT_OPTIONS = [12, 15, 20, 30];

const MODE_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Organic', value: 'organic' },
  { label: 'Paid', value: 'paid' },
];

const segmentGroupStyle = {
  display: 'flex',
  alignItems: 'center',
  borderRadius: 999,
  background: '#F1F3F9',
  border: `1px solid ${ui.borderSoft}`,
  padding: 3,
};

function Segment({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        cursor: 'pointer',
        border: 'none',
        outline: 'none',
        borderRadius: 999,
        background: active ? '#FFFFFF' : 'transparent',
        boxShadow: active ? '0 1px 3px rgba(16, 24, 40, 0.12)' : 'none',
        color: active ? ui.primary : ui.inkSoft,
        padding: '5px 13px',
        fontSize: 12,
        fontWeight: 700,
        fontFamily: ui.font,
        lineHeight: 1.5,
        transition: 'background 140ms ease, color 140ms ease',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function SegmentedGroup({ options, value, onChange, renderIcon }) {
  return (
    <div style={segmentGroupStyle}>
      {options.map((opt) => (
        <Segment key={String(opt.value)} active={value === opt.value} onClick={() => onChange(opt.value)}>
          {renderIcon ? renderIcon(opt, value === opt.value) : null}
          {opt.label}
        </Segment>
      ))}
    </div>
  );
}

function StatsFilters({ id, influencer, platform, filteredData, bc_public_data, isClicked, period: periodProp, setPeriod: setPeriodProp, contentType: contentTypeProp, setContentType: setContentTypeProp, is_brandconnect, onFilterTypeChange }) {
  const [filterViaTypes, filterViaTypes_set] = useState('all');
  const SetFilterViaTypes = (val) => { filterViaTypes_set(val); onFilterTypeChange?.(val); };
  const [periodLocal, setPeriodLocal] = useState('28');
  const [contentTypeLocal, setContentTypeLocal] = useState('overall');
  const [postCount, setPostCount] = useState(12);
  const [mode, setMode] = useState('all');

  const period = periodProp ?? periodLocal;
  const setPeriod = setPeriodProp ?? setPeriodLocal;
  const contentType = contentTypeProp ?? contentTypeLocal;
  const setContentType = setContentTypeProp ?? setContentTypeLocal;

  const dataSharingEnabled =
  String(bc_public_data?.dataSharingState || '')
    .toUpperCase()
    .includes('ENABLED') ||
  String(bc_public_data?.dataSharingState || '')
    .toUpperCase()
    .includes('GIVEN');
  const isBC = is_brandconnect === 'yes' && dataSharingEnabled;
  // The type param is only read from filterViaTypes on the non-BC path
  // (see typeParam below), so the toggle would be inert on a BC profile.
  const showYoutubeContentTabs = is_brandconnect !== 'yes' && platform === 'youtube';

  // `reel` is Instagram-only and `short` YouTube-only; without this a value
  // would survive a profile switch and be coerced back to `all` server-side,
  // leaving the highlighted tab lying about what is being shown.
  useEffect(() => {
    SetFilterViaTypes('all');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  useEffect(() => {
    if (id) {
      const typeParam = is_brandconnect === 'yes' ? contentType : filterViaTypes;
      const countParam = is_brandconnect !== 'yes' ? postCount : 12;
      fetchPostWiseBifurcation(
        id,
        mode === 'paid',
        typeParam,
        countParam,
        typeParam,
        countParam,
        period,
        mode === 'all',
        typeParam,
        countParam
      ).then((res) => filteredData(res));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterViaTypes, period, isClicked, id, platform, contentType, postCount, mode]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {isBC && (
        <SegmentedGroup options={CONTENT_OPTIONS} value={contentType} onChange={setContentType} />
      )}

      {platform === 'instagram' && (
        <SegmentedGroup
          options={[
            { value: 'all', label: 'All' },
            { value: 'post', label: 'Posts', icon: 'insta' },
            { value: 'reel', label: 'Reels', icon: 'video' },
          ]}
          value={filterViaTypes}
          onChange={SetFilterViaTypes}
          renderIcon={(opt, active) =>
            opt.icon === 'insta' ? (
              <InstagramIcon style={{ fontSize: '0.9rem', color: active ? ui.primary : ui.inkSoft }} />
            ) : opt.icon === 'video' ? (
              <VideoLibraryIcon style={{ fontSize: '0.9rem', color: active ? ui.primary : ui.inkSoft }} />
            ) : null
          }
        />
      )}

      {/* All / Videos / Shorts toggle — non-BC YouTube */}
      {showYoutubeContentTabs && (
        <SegmentedGroup
          options={YT_CONTENT_OPTIONS}
          value={filterViaTypes}
          onChange={SetFilterViaTypes}
          renderIcon={(opt, active) =>
            opt.value !== 'all' ? (
              <VideoLibraryIcon style={{ fontSize: '0.9rem', color: active ? ui.primary : ui.inkSoft }} />
            ) : null
          }
        />
      )}

      {/* All / Organic / Paid toggle */}
      {!isBC && <SegmentedGroup options={MODE_OPTIONS} value={mode} onChange={setMode} />}

      {/* Post count dropdown */}
      {!isBC && (
        <select
          value={postCount}
          onChange={(e) => setPostCount(Number(e.target.value))}
          style={{
            borderRadius: 999,
            background: '#F1F3F9',
            border: `1px solid ${ui.borderSoft}`,
            padding: '7px 12px',
            fontSize: 12,
            fontWeight: 700,
            fontFamily: ui.font,
            color: ui.inkSoft,
            cursor: 'pointer',
            outline: 'none',
            lineHeight: 1.5,
            appearance: 'auto',
          }}
        >
          {POST_COUNT_OPTIONS.map((count) => (
            <option key={count} value={count}>
              {count} posts
            </option>
          ))}
        </select>
      )}

      {/* Period tab */}
      {isBC && (
        <SegmentedGroup options={PERIOD_OPTIONS} value={period} onChange={setPeriod} />
      )}
    </div>
  );
}

export default StatsFilters;
